import {registerStyle, styleRule, UI, StyleSheet} from "../../../../stem-core/src/ui/UI";

import {authService} from "../../../../client/connection/Services";
import {AuthHelper} from "../../../AuthHelper";
import {LinkButton} from "../../../../core/ui/LinkButton.jsx";
import {Messages} from "../../../Messages";
import {Router} from "../../../../stem-core/src/ui/Router";
import {CONFIRMATION_CODE_TYPES, LOGIN_URL} from "../../../Constants";
import {ShortCodeEmailConfirmationForm} from "../components/ShortCodeEmailConfirmationForm";
import {LongCodeEmailConfirmationForm} from "../components/LongCodeEmailConfirmationForm";
import {AnalyticsEventType, dispatchAnalyticsEvent} from "../../../../blink-sdk/utils/AnalyticsClient";
import {authFormService} from "../../../services/AuthFormService";
import {MessageElement} from "../../../widget/ui/MessageElement";
import {LoadingSpinner} from "../../../../core/ui/LoadingSpinner.jsx";
import {Toast} from "../../../ui/Toast.jsx";
import {DEFAULT_TRANSITION} from "../../../../blink-sdk/Constants.js";


class LoginWithEmailCodeStyle extends StyleSheet {
    @styleRule
    container = {
        fontSize: this.themeProps.MERCHANT_FONT_SIZE_NORMAL,
    };

    @styleRule
    message = {
        color: this.themeProps.MERCHANT_TEXT_SECONDARY_COLOR,
        marginBottom: 14,
    };

    @styleRule
    text = {
        display: "inline",
    };

    @styleRule
    center = {
        display: "flex",
        justifyContent: "space-around",
        width: "100%",
    };

    @styleRule
    resendCode = {
        color: this.themeProps.MERCHANT_BUTTON_TERTIARY_TEXT_COLOR,
        transition: DEFAULT_TRANSITION,
        display: "inline-block",
        margin: "0 auto",
        padding: "0 18px",
        cursor: "pointer",
        userSelect: "none",
        textAlign: "center",
        width: this.themeProps.FIT_CONTENT,
        fontWeight: this.themeProps.MERCHANT_FONT_WEIGHT_REGULAR,
        fontSize: this.themeProps.MERCHANT_FONT_SIZE_LARGE,
        ":disabled": {
            color: this.themeProps.MERCHANT_BUTTON_TERTIARY_INACTIVE_TEXT_COLOR,
            cursor: "not-allowed",
        },
        ":disabled:hover": {
            color: this.themeProps.MERCHANT_BUTTON_TERTIARY_INACTIVE_TEXT_COLOR
        },
        background: this.themeProps.MERCHANT_BUTTON_TERTIARY_TEXT_COLOR,
        "-webkit-background-clip": "text",
        "-webkit-text-fill-color": "transparent",
        ":hover": {
            background: this.themeProps.MERCHANT_BUTTON_TERTIARY_TEXT_COLOR_HOVER,
            "-webkit-background-clip": "text",
        },
        paddingTop: 6,
    };

    @styleRule
    noPointerEvents = {
        pointerEvents: "none",
    };

    @styleRule
    invisible = {
        opacity: 0,
    };
}

@registerStyle(LoginWithEmailCodeStyle)
export class LoginWithEmailCode extends UI.Element {
    canDisplayResendCodeOption = true;
    confirmed = false;

    getDefaultOptions() {
        return {
            ...super.getDefaultOptions(),
            onWrongEmailButtonClick: () => Router.changeURL(LOGIN_URL), // TODO @auth @cleanup this won't be necessary once we merge for TNR
        }
    }

    getBeforeInput() {
        const {styleSheet, confirmed} = this;
        const messageFormatter = (authFormService.confirmationCodeType === CONFIRMATION_CODE_TYPES.SHORT) ?
            Messages.enterConfirmationCodeShort : Messages.enterConfirmationCodeLong;

        return [
            <MessageElement className={styleSheet.message} message={messageFormatter(authFormService.registrationMail)}/>,
            <LinkButton
                disabled={confirmed}
                onClick={() => this.handleWrongEmailLinkClick()}
                label={Messages.wrongEmail}
            />
        ];
    }

    getEmailConfirmationFormClass() {
        return authFormService.confirmationCodeType === CONFIRMATION_CODE_TYPES.LONG
            ? LongCodeEmailConfirmationForm : ShortCodeEmailConfirmationForm;
    }

    render() {
        const {styleSheet} = this;
        const EmailConfirmationFormClass = this.getEmailConfirmationFormClass();

        return [
            <div className={styleSheet.message}>
                {this.getBeforeInput()}
            </div>,
            <EmailConfirmationFormClass ref="emailConfirmationForm"
                                        onCodeAutomaticallyVerified={() => {
                                            this.canDisplayResendCodeOption = false;
                                            this.redraw();
                                        }}
                                        onSubmit={(code) => this.handleSubmit(code)}/>,
            this.getAfterInput(),
        ];
    }

    getAfterInput() {
        const {confirmed, canDisplayResendCodeOption, styleSheet} = this;

        if (!canDisplayResendCodeOption) {
            return null;
        }

        // TODO @mustfix use LinkButton here
        return <div className={styleSheet.center}>
            <div className={styleSheet.resendCode + (confirmed ? (styleSheet.noPointerEvents + styleSheet.invisible) : "")}
                 onClick={() => this.resendCode()}>
                {Messages.resendCode}
            </div>
        </div>
    }

    handleWrongEmailLinkClick() {
        if (this.emailConfirmationForm) {
            this.options.onWrongEmailButtonClick();
        }
    }

    async handleSubmit(code) {
        dispatchAnalyticsEvent(AnalyticsEventType.ATTEMPT_CONFIRM_EMAIL_CODE);
        LoadingSpinner.show()
        try {
            await this.confirmEmailCode(code);
            this.confirmed = true;
            this.onConfirmSuccess();
        } finally {
            LoadingSpinner.hide();
        }
    }

    onConfirmSuccess() {
        if (this.options.onSuccess) {
            this.options.onSuccess();
        }
        AuthHelper.confirmedAuthenticationAction();
    }

    async confirmEmailCode(code) {
        await AuthHelper.loginWithEmailCode(code);

        dispatchAnalyticsEvent(AnalyticsEventType.USER_LOGIN, {registration: authFormService.isRegistration});
    }

    async resendCode() {
        try {
            await AuthHelper.requestLoginCode();
            Toast.showSuccess(Messages.confirmationCodeResent);
        } catch (error) {
            Toast.showError(error, Messages.unexpectedError);
        }
    }

    onMount() {
        super.onMount();
        this.attachUpdateListener(authFormService, () => this.redraw());
        authService.executeWhenAuthenticated(() => {
            /*
            If the user completed email confirmation in another tab, redirect / close the current page.
            The timeout is needed because by confirming the code through this component the expected behaviour is not
            instant, and it is a bit hard to check whether or not the confirmation was made through this component
            otherwise.
             */
            setTimeout(() => {
                if (!this.confirmed) {
                    AuthHelper.confirmedAuthenticationAction();
                }
            }, 300);
        });
    }
}
